import React from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useLiveCountdown } from '@pages/RewardsHub/hooks/useTradingPoolProgress'
import { useTradingPoolCampaignResults } from '@pages/Traders-Room/Campaign-Results/Trading-Pools/hooks/useTradingPoolCampaignResults'

import { Body1, Body2, Caption1, Heading4, Heading6 } from '@ui/Typography'
import { Badge } from '@ui/components/Badge/Badge'
import { IconButton } from '@ui/components/Button'
import { Divider } from '@ui/components/Divider'
import { ProgressBar } from '@ui/components/ProgressBar'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { ChartBarHorizontalIcon } from '@ui/icons/ChartBarHorizontal.icon'
import { TableNoResults } from '@ui/v2/Table/Table'

import { TradingPoolCampaignStatus } from '@model/RewardsHubDashboardDto'

import { useFormatNumber } from '@hooks/useFormatNumber'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

import styles from './TradingPoolCard.module.scss'

export const TradingPoolCampaignClientResult: React.FC = () => {
  const {
    tradingPoolCampaignDetails,
    tradingPoolCampaignDetailsLoading,
    tradingPoolCampaignDetailsError,
    percentage,
  } = useTradingPoolCampaignResults()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const rewardsHubUrl = '/dashboard/rewards-hub/rewards'
  const daysRemaining = useLiveCountdown(tradingPoolCampaignDetails?.drawTime ?? '')
  const { formatNumber } = useFormatNumber()
  const tradingPoolCampaignDetailsStatusActive =
    tradingPoolCampaignDetails?.summary?.status?.id === TradingPoolCampaignStatus.ACTIVE

  const nextEntryIn =
    (tradingPoolCampaignDetails?.clientResult?.lotsPerEntry ?? 0) -
    (tradingPoolCampaignDetails?.clientResult?.progressToNextEntry ?? 0)

  if (tradingPoolCampaignDetailsError || !tradingPoolCampaignDetails?.clientResult) {
    return (
      <Box flex gap={'x8'} direction={'column'}>
        <Box gap={'x8'} flex align={'center'}>
          <IconButton.Secondary
            size='md'
            icon={ArrowIcon.Left}
            onClick={() => navigate(rewardsHubUrl)}
          />
          <Heading6 medium>{tradingPoolCampaignDetails?.campaign.name}</Heading6>
        </Box>
        <Divider.Horizontal />
        <Box flex align={'center'} gap={'x8'}>
          <ChartBarHorizontalIcon color={'text-high-em'} />
          <Heading6 medium color={'text-high-em'}>
            {t('RewardsHub.TradingPool.Your Progress')}
          </Heading6>
        </Box>
        <TableNoResults
          title={t('RewardsHub.TradingPool.No draw data to display')}
          content={
            <Body1 color='text-med-em'>{t('RewardsHub.TradingPool.Something went wrong')}</Body1>
          }
        />
      </Box>
    )
  }

  return (
    <Loading isLoading={tradingPoolCampaignDetailsLoading}>
      <Box direction={'column'}>
        <Box flex gap={'x8'}>
          <Box gap={'x8'} flex align={'center'}>
            <IconButton.Secondary
              size='md'
              icon={ArrowIcon.Left}
              onClick={() => navigate(rewardsHubUrl)}
            />
            <Heading6 medium>{tradingPoolCampaignDetails?.campaign.name}</Heading6>
          </Box>
          <Badge type={tradingPoolCampaignDetailsStatusActive ? 'green' : 'purple'} size={'xxs'}>
            {tradingPoolCampaignDetails?.summary?.status?.name}
          </Badge>
        </Box>
        <Divider.Horizontal />
      </Box>

      <Box flex justify={'space-between'} justifySelf={'center'}>
        <Box flex direction={'column'} align={'center'}>
          <Heading4 semibold>${tradingPoolCampaignDetails?.summary.prizePool}</Heading4>
          <Body2 color={'text-med-em'}>{t('RewardsHub.TradingPool.Prize Pool')}</Body2>
        </Box>
        <Box flex direction={'column'} align={'center'}>
          <Heading4 semibold>
            {tradingPoolCampaignDetailsStatusActive
              ? daysRemaining
              : tradingPoolCampaignDetails?.clientResult?.prizeInUsd === null
              ? '$0.00'
              : '$' + tradingPoolCampaignDetails?.clientResult?.prizeInUsd}
          </Heading4>
          <Body2 color={'text-med-em'}>
            {tradingPoolCampaignDetailsStatusActive
              ? t('RewardsHub.TradingPool.Draw in')
              : tradingPoolCampaignDetails?.clientResult?.prizeInUsd === null
              ? t('RewardsHub.TradingPool.Winnings')
              : t('RewardsHub.TradingPool.Congratulations!')}
          </Body2>
        </Box>
        <Box flex direction={'column'} align={'center'}>
          <Heading4 semibold>{tradingPoolCampaignDetails?.summary.totalEntries}</Heading4>
          <Body2 color={'text-med-em'}>{t('RewardsHub.TradingPool.Total entries')}</Body2>
        </Box>
        <Box flex direction={'column'} align={'center'}>
          <Heading4 semibold>{tradingPoolCampaignDetails?.summary.totalParticipants}</Heading4>
          <Body2 color={'text-med-em'}>{t('RewardsHub.TradingPool.Participants')}</Body2>
        </Box>
      </Box>

      <Box flex direction={'column'} gap={'x8'}>
        <Box flex align={'center'} gap={'x8'}>
          <ChartBarHorizontalIcon color={'text-high-em'} />
          <Heading6 medium color={'text-high-em'}>
            {t('RewardsHub.TradingPool.Your Progress')}
          </Heading6>
        </Box>
        <Divider.Horizontal />
      </Box>

      <Box
        flex
        direction={'column'}
        gap={'x16'}
        p={'x24'}
        className={styles.tradingPoolResultsCard}
      >
        <Box flex justify={'space-between'} justifySelf={'center'} gap={'x8'}>
          <Box
            flex
            flexItem={'1 0 0'}
            direction={'column'}
            gap={'x8'}
            className={styles.tradingPoolResultsCardItem}
          >
            <Caption1>{t('RewardsHub.TradingPool.Your rank')}</Caption1>
            <Body1 semibold>{tradingPoolCampaignDetails?.clientResult?.rank}</Body1>
          </Box>
          <Box
            flex
            flexItem={'1 0 0'}
            direction={'column'}
            gap={'x8'}
            className={styles.tradingPoolResultsCardItem}
          >
            <Caption1>{t('RewardsHub.TradingPool.Lots traded')}</Caption1>
            <Body1 semibold>{tradingPoolCampaignDetails?.clientResult?.lotsTraded}</Body1>
          </Box>
          <Box
            flex
            flexItem={'1 0 0'}
            direction={'column'}
            gap={'x8'}
            className={styles.tradingPoolResultsCardItem}
          >
            <Caption1>{t('RewardsHub.TradingPool.Entries earned')}</Caption1>
            <Body1 semibold>{tradingPoolCampaignDetails?.clientResult?.raffleEntries}</Body1>
          </Box>
          {tradingPoolCampaignDetailsStatusActive && (
            <Box
              flex
              flexItem={'1 0 0'}
              direction={'column'}
              gap={'x8'}
              className={styles.tradingPoolResultsCardItem}
            >
              <Caption1>{t('RewardsHub.TradingPool.Next entry in')}</Caption1>
              <Body1 semibold>
                {formatNumber(nextEntryIn, { minDecimalPlaces: 1, maxDecimalPlaces: 1 })}
              </Body1>
            </Box>
          )}
        </Box>
        {tradingPoolCampaignDetailsStatusActive && (
          <Box flex direction={'column'} gap={'x4'}>
            <ProgressBar color={'red'} percent={percentage} />
            <Box flex justify={'space-between'}>
              <Caption1 color={'text-med-em'}>
                {t('RewardsHub.TradingPool.Entry progress')}
              </Caption1>
              <Caption1 color={'text-med-em'}>
                {formatNumber(tradingPoolCampaignDetails?.clientResult.progressToNextEntry, {
                  minDecimalPlaces: 1,
                  maxDecimalPlaces: 1,
                })}
                {'/'}
                {formatNumber(tradingPoolCampaignDetails?.clientResult.lotsPerEntry, {
                  minDecimalPlaces: 1,
                  maxDecimalPlaces: 1,
                })}
              </Caption1>
            </Box>
          </Box>
        )}
      </Box>
    </Loading>
  )
}
